import App from 'models/app';
import { triggerError } from 'actions/gallery';
import { commsEventSupportedErrors, defaultError } from 'config';
import { HasSettingsStatus } from './constants';
import { translations } from 'i18n';

export function createNormalizedMap(
  newObjects = [],
  oldMap = {},
  keyProp = 'id'
) {
  const newMap = {};
  newObjects.forEach((obj) => {
    const mapKey = obj[keyProp];
    newMap[mapKey] = normalizeObjects(obj, oldMap[mapKey]);
  });
  return newMap;
}

export function normalizeObjects(newObj, oldObj) {
  if (oldObj) {
    return {
      ...oldObj,
      ...newObj
    };
  }
  return newObj;
}

export const handleCommsError = (dispatch, eventName, error) => {
  let localizedErrorInfo;
  const supportedErrors = commsEventSupportedErrors[eventName];
  if (supportedErrors && typeof error === 'string' && error.length) {
    const normalizedError = error.toLowerCase();
    localizedErrorInfo =
      supportedErrors.has(normalizedError) && normalizedError;
  }

  if (!localizedErrorInfo) {
    console.error(
      `Unknown error '${error}' encountered; falling back to default error: ${defaultError}`
    );
    localizedErrorInfo = defaultError;
  }

  dispatch(
    triggerError({
      titleKey: `comms.error.${localizedErrorInfo}.title`,
      messageKey: `comms.error.${localizedErrorInfo}.message`
    })
  );
};

export const handleFetchError = () => {
  const {
    fetch: { error: fetchErrorMap }
  } = translations;

  return {
    error: fetchErrorMap[defaultError].message
  };
};

export const pushApps = (existing = [], newApps = []) => [
  ...existing,
  ...newApps.map(({ id }) => id).filter((id) => !existing.includes(id))
];

export const getNextPageKey = ({ pages }) => {
  if (pages && pages.length > 1) {
    const page = pages.find(({ loaded }) => !loaded);

    if (!page) {
      return;
    }

    return page.key;
  }
};

export const hasNextPage = (pages) => {
  if (!pages || pages.length === 0) {
    return true;
  }

  return pages.some(({ loaded }) => !loaded);
};

export const updatePages = ({ pagedApps, pages = [], pageKey }) => {
  const nextPageKey = pagedApps.nextPages && pagedApps.nextPages[0];

  let hasCurrentPage = false;
  let hasNextPage = false;
  const newPages = pages.map((page) => {
    if (page.key === pageKey) {
      hasCurrentPage = true;
      return {
        key: pageKey,
        loaded: true
      };
    }

    if (nextPageKey && page.key === nextPageKey) {
      hasNextPage = true;
      return {
        key: nextPageKey,
        loaded: false
      };
    }

    return page;
  });

  if (!hasCurrentPage) {
    newPages.push({
      key: pageKey,
      loaded: true
    });
  }

  if (nextPageKey && !hasNextPage) {
    newPages.push({
      key: nextPageKey,
      loaded: false
    });
  }

  return newPages;
};

const shouldNotFetchHasSettings = (app, settingsStatus) => {
  if (!app) {
    return true;
  }

  // Not installed so no need to check yet
  if (!app.isOnDevice) {
    return true;
  }

  // Don't ask for settings if app has a companion already
  // except for the first time in case there is an override
  // for the app from mobile e.g. Halo and Pandora
  if (settingsStatus && !app.hasCompanion) {
    return true;
  }

  // Already checked for settings
  if (
    settingsStatus &&
    settingsStatus.hasSettings !== HasSettingsStatus.NO_COMPANION
  ) {
    return true;
  }

  return false;
};

export const fetchHasSettings = async (appIds, state) => {
  const { settings } = state.gallery;
  const newSettings = {};

  for (const appId of appIds) {
    const app = App.selectFromState(appId, state);

    // If app is not installed or has already been checked, no reason to ask for settings
    if (shouldNotFetchHasSettings(app, settings[appId])) {
      continue;
    }

    const buildId = app.buildId;

    let { hasSettings } = await window.__webview_comms__.get('has-settings', {
      appId,
      buildId
    });
    // TODO: Android is on old spec
    if (hasSettings === 'true' || hasSettings === true) {
      hasSettings = HasSettingsStatus.YES;
    } else if (hasSettings === 'false' || hasSettings === false) {
      hasSettings = HasSettingsStatus.NO;
    } else if (hasSettings === 'null' || hasSettings === null) {
      hasSettings = HasSettingsStatus.NO_COMPANION;
    }

    newSettings[appId] = { appId, buildId, hasSettings };
  }

  return newSettings;
};
